import {
  ADD_BUILDINGS,
  START_BUILDINGS_LOAD,
  INVALIDATE_BUILDINGS,
  DELETE_ALL_BUILDINGS,
  ADD_MULTIPLE_LOCATIONS,
  DELETE_MULTIPLE_LOCATIONS,
} from './actiontypes';
import { addMultipleEquipmentAndPointsAction } from './equipment';
import {
  getAll,
  patchBuilding,
  deleteBuilding,
  createBuilding,
  buildingDbo,
  getAllLocationsForBuilding,
  createLinkRelationships,
  deleteLinkRelationships,
  createLocationApi,
  modifyLocationsApi,
  deleteLocationApi,
} from '../../api/Api';
import { downloadBlob } from '../../utils/downloads';
import getApiBaseUrl from '../../api/urls';

export function deleteAllBuildingsAction() {
  return {
    type: DELETE_ALL_BUILDINGS,
  };
}

const startBuildingsLoadingAction = () => ({
  type: START_BUILDINGS_LOAD,
});

// an action that describes which state to update when
// adding buildings
function addBuildingsAction(buildings) {
  return {
    type: ADD_BUILDINGS,
    buildings,
    receivedAt: Date.now(),
  };
}

// eslint-disable-next-line import/no-unused-modules
export function invalidateBuildingsAction() {
  return {
    type: INVALIDATE_BUILDINGS,
  };
}

// an action creator which handles making the GET buildings network request
// and dispatching an action once network response/error returns
export default function fetchBuildingsActionCreator() {
  // Redux Thunk allows this, see its docs for more detail
  return (dispatch, getState) => {
    const state = getState();

    // if current buildings already fetched and  not invalidated, abort fetch
    if (
      (!state.entities.buildings.meta.isInvalid && state.entities.buildings.meta.receivedAt) ||
      state.entities.buildings.meta.isLoading
    ) {
      return Promise.resolve();
    }
    dispatch(startBuildingsLoadingAction());

    return getAll(state.user.jwt, 'buildings').then(
      (response) => {
        dispatch(addBuildingsAction(response));
      },
      // eslint-disable-next-line no-console
      (error) => console.log(error)
    );
  };
}

export const patchBuildingActionCreator = (buildingId, patch) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();

  return (
    patchBuilding(buildingId, patch, jwt)
      // eslint-disable-next-line no-console
      .catch((e) => console.error(`Error patching building ${e}`))
      .finally(() => {
        dispatch(invalidateBuildingsAction());
        dispatch(fetchBuildingsActionCreator());
      })
  );
};

export const createBuildingActionCreator =
  ({ name, orgId, address, purpose, area, areaUnitId }) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();

    return createBuilding(jwt, name, orgId, address, purpose, area, areaUnitId).then((building) => {
      dispatch(addBuildingsAction([building]));
      return building;
    });
  };

export const deleteBuildingActionCreator = (buildingId) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();

  return (
    deleteBuilding(buildingId, jwt)
      // eslint-disable-next-line no-console
      .catch((e) => console.error(`Error deleting building ${e}`))
      .finally(() => {
        dispatch(deleteAllBuildingsAction());
        dispatch(fetchBuildingsActionCreator());
      })
  );
};

export const fetchBuildingDbo = (buildingId) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return buildingDbo(buildingId, jwt)
    .then((blob) => downloadBlob(blob, `dbo-${buildingId}.yaml`))
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.error(`Error downloading building config, opening raw: ${e}`);
      window.location.replace(`${getApiBaseUrl()}/buildings/${buildingId}/dbo`);
    });
};

function addMultipleLocationsAction(buildingId, { locations }) {
  return {
    type: ADD_MULTIPLE_LOCATIONS,
    buildingId,
    locations,
  };
}

function deleteLocationsAction(buildingId, { locations_deleted }) {
  return {
    type: DELETE_MULTIPLE_LOCATIONS,
    buildingId,
    locations_deleted,
  };
}

export const fetchLocationsForBuildingAction =
  (buildingId, force = false) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();

    const state = getState();
    const locations = state.entities.buildings.locationsByBuildingIdName[buildingId];
    // If we have already loaded into redux, don't reload
    if (locations && !force) return Promise.resolve();
    return getAllLocationsForBuilding(buildingId, jwt).then((response) => {
      // dispatch action to add locations
      dispatch(addMultipleLocationsAction(buildingId, response));
    });
  };

export const modifyRelations =
  (buildingId, type, createPayload, deletePayload) => (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();
    let promise = Promise.resolve();
    if (createPayload.length > 0) {
      promise = promise
        .then(() => createLinkRelationships(jwt, buildingId, type, createPayload))
        .then((response) =>
          Promise.all([
            dispatch(addMultipleLocationsAction(buildingId, response)),
            dispatch(addMultipleEquipmentAndPointsAction(response)),
          ])
        );
    }
    if (deletePayload.length > 0) {
      promise = promise
        .then(() => deleteLinkRelationships(jwt, buildingId, type, deletePayload))
        .then((response) =>
          Promise.all([
            dispatch(addMultipleLocationsAction(buildingId, response)),
            dispatch(addMultipleEquipmentAndPointsAction(response)),
          ])
        );
    }
    return promise;
  };

export const createLocation = (buildingId, name, type) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return createLocationApi(jwt, buildingId, name, type).then((response) =>
    dispatch(addMultipleLocationsAction(buildingId, { locations: [response] }))
  );
};

export const modifyLocations = (buildingId, payload) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return modifyLocationsApi(jwt, buildingId, payload).then((response) =>
    Promise.all([
      dispatch(addMultipleLocationsAction(buildingId, response)),
      dispatch(
        deleteLocationsAction(buildingId, { locations_deleted: payload.map((pair) => pair.name) })
      ),
    ])
  );
};

export const deleteLocation = (buildingId, name) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return deleteLocationApi(jwt, buildingId, name).then((response) =>
    dispatch(deleteLocationsAction(buildingId, response))
  );
};
